import React, { useState } from 'react'
import { ArrowRight, Loader2, Plus, SkipForward, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import {
  TagInput,
  LocationAutocomplete,
  INDUSTRIES,
  COMPANY_SIZES,
  upsertIdentity,
  type IdentityProfile,
  type ProductOrService,
  type ValueProposition,
} from '@/features/company-profile'
import { JOB_TITLES } from '@/features/company-profile/data/jobTitles'
import { createIcp } from '@/features/icps/services/icpsApi'
import type { WorkspaceSummary } from '../services/workspaceApi'

interface Props {
  workspace: WorkspaceSummary
}

const CompleteProfileStep: React.FC<Props> = ({ workspace }) => {
  const { currentTheme: t } = useTheme()
  const navigate = useNavigate()

  const [identity, setIdentity] = useState<IdentityProfile>({
    companyName: workspace.name,
    productsServices: [],
    valuePropositions: [],
    targetCustomerProfile: { industries: [], companySizes: [], jobTitles: [] },
    competitors: [],
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const goToDashboard = () => navigate('/dashboard', { replace: true })

  const updateField = <K extends keyof IdentityProfile>(key: K, value: IdentityProfile[K]) => {
    setIdentity((prev) => ({ ...prev, [key]: value }))
  }

  const updateProductsServices = (rows: ProductOrService[]) => updateField('productsServices', rows)
  const updateValueProps = (rows: ValueProposition[]) => updateField('valuePropositions', rows)

  // Rows added via "+ Add" but left blank shouldn't block the save — drop only
  // fully-blank rows, same tolerance as Settings' Company Profile form.
  // The ICP isn't part of the company profile any more — it is saved separately as the
  // workspace's first ICP (see handleFinish), and the profile API rejects it on this payload.
  const buildSavePayload = (): IdentityProfile => {
    const { targetCustomerProfile: _icp, ...rest } = identity
    return {
      ...rest,
      productsServices: (identity.productsServices ?? []).filter((r) => r.name.trim() || r.description.trim()),
      valuePropositions: (identity.valuePropositions ?? []).filter((r) => r.title.trim() || r.description.trim()),
    }
  }

  const handleSkip = () => goToDashboard()

  const handleFinish = async () => {
    setSaving(true)
    setError(null)
    try {
      await upsertIdentity(workspace.id, buildSavePayload())
      // A brand-new workspace has no ICPs, so what was entered becomes its first (primary) one.
      const icp = identity.targetCustomerProfile
      if (icp && (icp.industries.length > 0 || icp.companySizes.length > 0 || icp.jobTitles.length > 0)) {
        await createIcp(workspace.id, {
          name: 'Primary',
          industries: icp.industries,
          companySizes: icp.companySizes,
          jobTitles: icp.jobTitles,
        })
      }
      goToDashboard()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = { backgroundColor: t.inputBackground, color: t.text, borderColor: t.border }
  const labelClass = 'block text-sm font-semibold mb-2'
  const inputClass = 'w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition'
  const addButtonClass = 'flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition'

  return (
    <div className="p-6 sm:p-8 xl:p-10 flex flex-col h-full">
      <div className="mb-6 shrink-0">
        <span
          className="inline-block mb-3 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide uppercase"
          style={{ backgroundColor: `${t.accent}18`, color: t.accent }}
        >
          Step 2 of 2
        </span>
        <h1 className="text-3xl font-semibold sm:text-4xl" style={{ color: t.text }}>
          Complete your company profile
        </h1>
        <p className="mt-2 text-base leading-7" style={{ color: t.secondrytext }}>
          This helps Zeph personalize research, qualification, and outreach. Fill in as much or as
          little as you like — you can always finish this later in Settings.
        </p>
      </div>

      {error && (
        <div
          className="mb-5 rounded-2xl px-5 py-4 text-sm shrink-0"
          style={{ backgroundColor: t.errorBackground, color: t.errorText, border: `1px solid ${t.border}` }}
        >
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1">
        {/* Basics */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>Basics</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} style={{ color: t.text }}>Company name</label>
              <input
                value={identity.companyName ?? ''}
                onChange={(e) => updateField('companyName', e.target.value)}
                placeholder="e.g. Zeph AI"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>Website</label>
              <input
                value={identity.website ?? ''}
                onChange={(e) => updateField('website', e.target.value)}
                placeholder="https://example.com"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass} style={{ color: t.text }}>HQ location</label>
              <LocationAutocomplete
                value={identity.hqLocation ?? ''}
                onChange={(value) => updateField('hqLocation', value)}
                placeholder="e.g. Bengaluru, India"
              />
            </div>
          </div>
        </section>

        {/* Positioning */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>Positioning</h3>
          <div className="space-y-4">
            <div>
              <label className={labelClass} style={{ color: t.text }}>
                One-liner
                <span className="ml-2 font-normal" style={{ color: t.mutedText }}>{(identity.oneLiner ?? '').length}/160</span>
              </label>
              <input
                value={identity.oneLiner ?? ''}
                onChange={(e) => updateField('oneLiner', e.target.value.slice(0, 160))}
                placeholder="One sentence describing what you do"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>
                About us
                <span className="ml-2 font-normal" style={{ color: t.mutedText }}>{(identity.aboutUs ?? '').length}/2000</span>
              </label>
              <textarea
                value={identity.aboutUs ?? ''}
                onChange={(e) => updateField('aboutUs', e.target.value.slice(0, 2000))}
                placeholder="A longer description of your company and what it offers"
                rows={3}
                className={inputClass}
                style={inputStyle}
              />
            </div>
          </div>
        </section>

        {/* Products & Services */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold" style={{ color: t.text }}>Products & Services</h3>
            <button
              type="button"
              onClick={() => updateProductsServices([...(identity.productsServices ?? []), { name: '', description: '' }])}
              className={addButtonClass}
              style={{ backgroundColor: t.hoverBackground, color: t.text }}
            >
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="space-y-3">
            {(identity.productsServices ?? []).length === 0 && (
              <p className="text-sm" style={{ color: t.mutedText }}>What do you sell?</p>
            )}
            {(identity.productsServices ?? []).map((row, idx) => (
              <div key={idx} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] items-start rounded-xl border p-3" style={{ borderColor: t.border }}>
                <input
                  value={row.name}
                  onChange={(e) => {
                    const next = [...(identity.productsServices ?? [])]
                    next[idx] = { ...row, name: e.target.value }
                    updateProductsServices(next)
                  }}
                  placeholder="Name"
                  className={inputClass}
                  style={inputStyle}
                />
                <input
                  value={row.description}
                  onChange={(e) => {
                    const next = [...(identity.productsServices ?? [])]
                    next[idx] = { ...row, description: e.target.value }
                    updateProductsServices(next)
                  }}
                  placeholder="Short description"
                  className={inputClass}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => updateProductsServices((identity.productsServices ?? []).filter((_, i) => i !== idx))}
                  className="rounded-lg p-2.5 transition hover:opacity-70"
                  style={{ color: t.errorText }}
                  aria-label="Remove"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Value Propositions */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold" style={{ color: t.text }}>Value Propositions</h3>
            <button
              type="button"
              onClick={() => updateValueProps([...(identity.valuePropositions ?? []), { title: '', description: '' }])}
              className={addButtonClass}
              style={{ backgroundColor: t.hoverBackground, color: t.text }}
            >
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="space-y-3">
            {(identity.valuePropositions ?? []).length === 0 && (
              <p className="text-sm" style={{ color: t.mutedText }}>What pain do you solve, and for whom?</p>
            )}
            {(identity.valuePropositions ?? []).map((row, idx) => (
              <div key={idx} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] items-start rounded-xl border p-3" style={{ borderColor: t.border }}>
                <input
                  value={row.title}
                  onChange={(e) => {
                    const next = [...(identity.valuePropositions ?? [])]
                    next[idx] = { ...row, title: e.target.value }
                    updateValueProps(next)
                  }}
                  placeholder="Title"
                  className={inputClass}
                  style={inputStyle}
                />
                <input
                  value={row.description}
                  onChange={(e) => {
                    const next = [...(identity.valuePropositions ?? [])]
                    next[idx] = { ...row, description: e.target.value }
                    updateValueProps(next)
                  }}
                  placeholder="Description"
                  className={inputClass}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => updateValueProps((identity.valuePropositions ?? []).filter((_, i) => i !== idx))}
                  className="rounded-lg p-2.5 transition hover:opacity-70"
                  style={{ color: t.errorText }}
                  aria-label="Remove"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Target Customer Profile */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-1" style={{ color: t.text }}>Target Customer Profile</h3>
          <p className="text-xs mb-4" style={{ color: t.mutedText }}>
            This becomes your first ideal customer profile (ICP). You can add more for other segments later in Sales → ICPs.
          </p>
          <div className="space-y-4">
            <div>
              <label className={labelClass} style={{ color: t.text }}>Industries</label>
              <TagInput
                value={identity.targetCustomerProfile?.industries ?? []}
                onChange={(industries) => updateField('targetCustomerProfile', {
                  industries,
                  companySizes: identity.targetCustomerProfile?.companySizes ?? [],
                  jobTitles: identity.targetCustomerProfile?.jobTitles ?? [],
                })}
                suggestions={INDUSTRIES}
                placeholder="e.g. SaaS / Software"
              />
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>Company sizes</label>
              <TagInput
                value={identity.targetCustomerProfile?.companySizes ?? []}
                onChange={(companySizes) => updateField('targetCustomerProfile', {
                  industries: identity.targetCustomerProfile?.industries ?? [],
                  companySizes,
                  jobTitles: identity.targetCustomerProfile?.jobTitles ?? [],
                })}
                suggestions={COMPANY_SIZES}
                placeholder="e.g. 11–50"
              />
            </div>
            <div>
              <label className={labelClass} style={{ color: t.text }}>Job titles</label>
              <TagInput
                value={identity.targetCustomerProfile?.jobTitles ?? []}
                onChange={(jobTitles) => updateField('targetCustomerProfile', {
                  industries: identity.targetCustomerProfile?.industries ?? [],
                  companySizes: identity.targetCustomerProfile?.companySizes ?? [],
                  jobTitles,
                })}
                suggestions={JOB_TITLES}
                placeholder="e.g. Head of Sales"
              />
            </div>
          </div>
        </section>

        {/* Differentiators */}
        <section className="pb-6 mb-6 border-b" style={{ borderColor: t.border }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>Differentiators</h3>
          <textarea
            value={identity.differentiators ?? ''}
            onChange={(e) => updateField('differentiators', e.target.value.slice(0, 1000))}
            placeholder="What makes you different from alternatives?"
            rows={2}
            className={inputClass}
            style={inputStyle}
          />
        </section>

        {/* Competitors */}
        <section>
          <h3 className="text-base font-semibold mb-4" style={{ color: t.text }}>
            Competitors <span className="font-normal text-sm" style={{ color: t.mutedText }}>(optional)</span>
          </h3>
          <TagInput
            value={identity.competitors ?? []}
            onChange={(competitors) => updateField('competitors', competitors)}
            placeholder="e.g. Competitor A"
          />
        </section>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 shrink-0" style={{ borderTop: `1px solid ${t.border}` }}>
        <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleSkip}
            disabled={saving}
            className="flex items-center justify-center gap-2 h-10 px-4 rounded-full text-[13.5px] font-semibold transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 shrink-0"
            style={{ color: t.mutedText }}
          >
            <SkipForward size={14} />
            Skip for now
          </button>
          <button
            type="button"
            onClick={() => void handleFinish()}
            disabled={saving}
            className="flex items-center justify-center gap-2 h-11 px-6 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 shrink-0"
            style={{ background: t.accentGradient, boxShadow: '0 1px 6px rgba(79,70,229,0.25)' }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {saving ? 'Saving…' : 'Finish setup'}
          </button>
        </div>
        <p className="mt-3 text-xs text-center sm:text-right" style={{ color: t.mutedText }}>
          You can complete this anytime from Settings.
        </p>
      </div>
    </div>
  )
}

export default React.memo(CompleteProfileStep)
